import {
  CUSTOMER_SUPPORT_DATA_ACCESS,
  type IntentPolicyDefinition,
} from "@/api/mock-data/investment-banking/intentPolicies";
import { DEMO_MCP_SERVER_CATALOG } from "@/api/mock-data/investment-banking/mcpServers";
import {
  DEMO_ADVISORY_RECOMMENDATIONS,
  DEMO_SCENARIOS,
} from "@/api/mock-data/investment-banking/scenarios";
import {
  AgentBusinessUnit,
  AgentClassificationEvidence,
  AgentEnvironmentType,
  AgentMissionCandidate,
  AgentMissionContextChange,
  AgentMissionEvidence,
  AgentMissionStatus,
  AgentMissionSuggestion,
  AgentPlatform,
  AgentPlatformCatalogEntry,
  AgentPolicyRecommendation,
  AgentProject,
  AgentPurpose,
  AgentSecurityStatus,
  AgentTechnology,
  EstateAgentToolClassification,
  PolicyCatalogItem,
  PolicyCategory,
  PolicyControl,
  PolicyControlSense,
} from "@/api/temp/AgentEstateService";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";

/**
 * Vocabularies and target distributions the agent-estate generator draws from.
 *
 * Every number a presenter can point at comes from here. The estate is exactly
 * 14 agents - 9 Secure, 5 Not Secure, 4 of which have a recommendation
 * waiting - all discovered from one connection, so "14 agents discovered"
 * during the run, the summary header, the sum of the category cards and the
 * unfiltered list are all the same 14 records.
 *
 * Themed as a mid-size investment bank: business units are relabeled (Human
 * Capital, Private Banking & Client Services, Markets & Trading, Technology &
 * Trading Systems, Risk & Quant Research) but the underlying enum values
 * (`AgentBusinessUnit`, `AgentPurpose`, `AgentTechnology`) are untouched, so
 * nothing outside this file needed a type change.
 */

export const PLATFORM_CATALOG: AgentPlatformCatalogEntry[] = [
  {
    id: "MICROSOFT_FOUNDRY",
    name: "Microsoft Azure AI Foundry",
    description:
      "Agents, models, tools and runtime metadata from Azure AI Foundry.",
    recommended: true,
    availability: "AVAILABLE",
    detectedIdentityProvider: "MICROSOFT_ENTRA_ID",
    sampleConnection: {
      connectionName: "Production Foundry",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "MICROSOFT_365",
    name: "Microsoft 365 / Work IQ",
    description:
      "Copilot agents, connectors and workspace content from Microsoft 365.",
    recommended: false,
    availability: "COMING_SOON",
    detectedIdentityProvider: "MICROSOFT_ENTRA_ID",
    sampleConnection: {
      connectionName: "Northstar Microsoft 365",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "OPENAI",
    name: "OpenAI",
    description:
      "Assistants, tools and project metadata from an OpenAI organization.",
    recommended: false,
    availability: "COMING_SOON",
    sampleConnection: {
      connectionName: "Enterprise OpenAI",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "DATABRICKS",
    name: "Databricks",
    description: "Databricks workspaces, agents, tools and model context.",
    recommended: false,
    availability: "BETA",
    selectable: true,
    sampleConnection: {
      connectionName: "AI Databricks",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "SNOWFLAKE",
    name: "Snowflake",
    description:
      "Snowflake agents, data context and platform-local identities.",
    recommended: false,
    availability: "BETA",
    selectable: true,
    sampleConnection: {
      connectionName: "Enterprise Snowflake",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "GLEAN",
    name: "Glean",
    description: "Glean agents, actions and the enterprise knowledge they reach.",
    recommended: false,
    availability: "BETA",
    selectable: true,
    sampleConnection: {
      connectionName: "Enterprise Glean",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
  {
    id: "AWS_AGENTCORE",
    name: "AWS AgentCore",
    description: "AWS agent runtime, skills and relationship inventory.",
    recommended: false,
    availability: "COMING_SOON",
    sampleConnection: {
      connectionName: "AgentCore Production",
      account: "northstar-financial",
      clientId: "00000000-0000-0000-0000-000000000000",
    },
  },
];

export const PLATFORM_LABELS: Record<AgentPlatform, string> = {
  MICROSOFT_FOUNDRY: "Microsoft Azure AI Foundry",
  MICROSOFT_365: "Microsoft 365 / Work IQ",
  OPENAI: "OpenAI",
  DATABRICKS: "Databricks",
  SNOWFLAKE: "Snowflake",
  AWS_AGENTCORE: "AWS AgentCore",
  GLEAN: "Glean",
};

export const PLATFORM_ORDER: AgentPlatform[] = [
  "MICROSOFT_FOUNDRY",
  "MICROSOFT_365",
  "DATABRICKS",
  "SNOWFLAKE",
  "GLEAN",
  "OPENAI",
  "AWS_AGENTCORE",
];

export const BUSINESS_UNIT_LABELS: Record<AgentBusinessUnit, string> = {
  HR: "Human Capital",
  CUSTOMER_SUPPORT: "Private Banking & Client Services",
  FINANCE: "Markets & Trading",
  ENGINEERING: "Technology & Trading Systems",
  ANALYTICS: "Risk & Quant Research",
};

/** Order the category cards list business units in. */
export const BUSINESS_UNIT_ORDER: AgentBusinessUnit[] = [
  "HR",
  "CUSTOMER_SUPPORT",
  "FINANCE",
  "ENGINEERING",
  "ANALYTICS",
];

export const PURPOSE_LABELS: Record<AgentPurpose, string> = {
  ENGINEERING: "Engineering",
  ANALYTICS: "Analytics",
  RESEARCH: "Research",
  HR: "HR",
  FINANCE: "Finance",
  CUSTOMER_SUPPORT: "Customer Support",
  CONTENT_CREATION: "Content Creation",
};

export const PURPOSE_ORDER: AgentPurpose[] = [
  "ENGINEERING",
  "ANALYTICS",
  "RESEARCH",
  "HR",
  "FINANCE",
  "CUSTOMER_SUPPORT",
  "CONTENT_CREATION",
];

/** The free-text sentence pool, per classified purpose. */
export const PURPOSE_DESCRIPTIONS: Record<AgentPurpose, string[]> = {
  ENGINEERING: [
    "Trading platform code review and pull-request assistant",
    "Release and deployment triage assistant for trading systems",
  ],
  ANALYTICS: ["Risk and P&L analytics reporting copilot"],
  RESEARCH: ["Equity and credit research synthesis"],
  HR: [
    "Client Onboarding (KYC) — verifies new client documentation and eligibility",
    "Compensation & Benefits — explains benefits and eligibility",
    "Employee Support — answers workplace policy questions",
  ],
  FINANCE: [
    "Trading desk P&L reporting assistant",
    "Counterparty exposure and margin analysis assistant",
  ],
  CUSTOMER_SUPPORT: [
    "Private banking client service assistant",
    "Employee and customer support — answers service questions",
  ],
  CONTENT_CREATION: ["Relationship manager correspondence drafting assistant"],
};

/** The six resources agents in this estate reach. */
export const CONNECTED_RESOURCES: readonly AgentTechnology[] = [
  "SharePoint",
  "Snowflake",
  "Databricks",
  "Internal Knowledge Base",
  "GitHub",
  "Microsoft 365",
] as const;

/**
 * One slice of a business unit: a purpose, the technology those agents reach,
 * and how many of them are settled Secure vs Not Secure.
 *
 * This table is the single source of truth for every headline number. Totals
 * are summed from it (see `unitTotals` / `estateTotals`) rather than
 * hand-maintained alongside it, so the arithmetic cannot drift.
 *
 * It adds up to 14 agents - 9 Secure, 5 Not Secure. Of the 5 Not Secure,
 * the 1 Research agent matches no recommendation in RECOMMENDATION_CATALOG,
 * which is what leaves exactly 4 agents with a recommendation waiting and
 * gives the Custom Policy tab a real reason to exist.
 */
export interface PurposeMixRow {
  purpose: AgentPurpose;
  technology: AgentTechnology;
  secure: number;
  notSecure: number;
}

export interface BusinessUnitDefinition {
  purposeMix: PurposeMixRow[];
  /**
   * The delivery initiatives this unit runs, cycled over in generation order.
   * Two of them (`Client Onboarding (KYC)`, `Trade Surveillance`) are
   * deliberately shared with another unit, so grouping by PROJECT produces a
   * genuinely different partition instead of a re-labelled BUSINESS_UNIT.
   */
  projects: AgentProject[];
}

/**
 * How one connected environment's agents spread across deployment tiers.
 *
 * A connection declares a single `environmentType`, but it hosts agents at
 * several tiers — a production Foundry connection still carries the test and
 * development agents built against it. The cycle is fixed rather than random so
 * the per-tier counts stay exact and reproducible for the specs, and it never
 * promotes an agent above its connection: a TEST connection has no production
 * agents.
 */
export const ENVIRONMENT_TIER_MIX: Record<
  AgentEnvironmentType,
  AgentEnvironmentType[]
> = {
  PRODUCTION: [
    "PRODUCTION",
    "PRODUCTION",
    "PRODUCTION",
    "TEST",
    "PRODUCTION",
    "DEVELOPMENT",
    "PRODUCTION",
    "TEST",
  ],
  TEST: ["TEST", "TEST", "DEVELOPMENT"],
  DEVELOPMENT: ["DEVELOPMENT"],
};

/** Display order for the estate's Project filter. */
export const PROJECT_OPTIONS: readonly AgentProject[] = [
  "Client Onboarding (KYC)",
  "Wealth Advisory Portal",
  "Trade Surveillance",
  "Trading Platform Modernization",
  "Research Publishing",
];

export const BUSINESS_UNIT_DEFINITIONS: Record<
  AgentBusinessUnit,
  BusinessUnitDefinition
> = {
  HR: {
    projects: ["Client Onboarding (KYC)"],
    purposeMix: [
      { purpose: "HR", technology: "SharePoint", secure: 2, notSecure: 1 },
    ],
  },
  CUSTOMER_SUPPORT: {
    projects: ["Client Onboarding (KYC)", "Wealth Advisory Portal"],
    purposeMix: [
      {
        purpose: "CUSTOMER_SUPPORT",
        technology: "Internal Knowledge Base",
        secure: 1,
        notSecure: 1,
      },
      {
        purpose: "CONTENT_CREATION",
        technology: "Microsoft 365",
        secure: 1,
        notSecure: 0,
      },
    ],
  },
  FINANCE: {
    projects: ["Trade Surveillance"],
    purposeMix: [
      { purpose: "FINANCE", technology: "Snowflake", secure: 2, notSecure: 0 },
      { purpose: "FINANCE", technology: "Databricks", secure: 0, notSecure: 1 },
    ],
  },
  ENGINEERING: {
    projects: ["Trade Surveillance", "Trading Platform Modernization"],
    // Exactly 2 on GitHub, so "2 Engineering agents · GitHub" is literal.
    purposeMix: [
      { purpose: "ENGINEERING", technology: "GitHub", secure: 1, notSecure: 1 },
      {
        purpose: "ENGINEERING",
        technology: "Databricks",
        secure: 1,
        notSecure: 0,
      },
    ],
  },
  ANALYTICS: {
    projects: ["Research Publishing"],
    purposeMix: [
      {
        purpose: "ANALYTICS",
        technology: "Snowflake",
        secure: 1,
        notSecure: 0,
      },
      // The deliberate gap: no recommendation covers Research agents.
      {
        purpose: "RESEARCH",
        technology: "Internal Knowledge Base",
        secure: 0,
        notSecure: 1,
      },
    ],
  },
};

export const unitTotals = (
  businessUnit: AgentBusinessUnit,
): { total: number; secure: number; notSecure: number } =>
  BUSINESS_UNIT_DEFINITIONS[businessUnit].purposeMix.reduce(
    (totals, row) => ({
      total: totals.total + row.secure + row.notSecure,
      secure: totals.secure + row.secure,
      notSecure: totals.notSecure + row.notSecure,
    }),
    { total: 0, secure: 0, notSecure: 0 },
  );

export const estateTotals = (): {
  total: number;
  secure: number;
  notSecure: number;
} =>
  BUSINESS_UNIT_ORDER.reduce(
    (totals, businessUnit) => {
      const unit = unitTotals(businessUnit);
      return {
        total: totals.total + unit.total,
        secure: totals.secure + unit.secure,
        notSecure: totals.notSecure + unit.notSecure,
      };
    },
    { total: 0, secure: 0, notSecure: 0 },
  );

export const REGIONS = [
  "US East",
  "US West",
  "EU West",
  "EU Central",
  "AP Southeast",
];

export const OWNERS = [
  "Priya Raman",
  "Marcus Webb",
  "Dana Okafor",
  "Ilya Sorokin",
  "Grace Lin",
  "Tomas Nowak",
];

export const MODELS = [
  "gpt-4o",
  "claude-opus-5",
  "llama-3.1-70b",
  "mistral-large",
];

export const AGENT_NAME_PREFIXES = [
  "Atlas",
  "Beacon",
  "Cascade",
  "Delta",
  "Ember",
  "Forge",
  "Harbor",
  "Ionic",
  "Keystone",
  "Lumen",
  "Meridian",
  "Northstar",
  "Orbit",
  "Pinnacle",
  "Quarry",
  "Relay",
  "Summit",
  "Tessera",
  "Vantage",
  "Zenith",
];

/**
 * One MCP server a business unit's agents reach. Scoped rather than
 * technology-generic - two agents on the same technology (Snowflake for
 * Markets vs. for Risk & Quant) can carry entirely different tool surfaces,
 * and collapsing them into one bucket per technology made every agent on that
 * technology look identical in the detail tabs.
 *
 * `businessUnits` empty means "fallback for this technology" - it applies to
 * an agent whose business unit no scoped server names. `buildMcpServices`
 * (in `./store`) resolves an agent to exactly one server: a scoped match
 * wins over a fallback.
 */
export interface McpServerDefinition {
  id: string;
  name: string;
  technology: AgentTechnology;
  /** Which business units' agents this server is scoped to; empty = general/fallback for its technology. */
  businessUnits: AgentBusinessUnit[];
  capabilities: string[];
  tools: Array<{
    name: string;
    classification: EstateAgentToolClassification;
    dataAccess: string;
    custom?: boolean;
  }>;
}

/**
 * Every MCP server discovery has found, which is not the same set the
 * generated estate reaches.
 *
 * Three entries — `mcp-sharepoint-compliance`, `mcp-snowflake-warehouse` and
 * `mcp-kb-hr-benefits` — are deliberately unreachable from the current
 * `BUSINESS_UNIT_DEFINITIONS` purpose mix: `resolveMcpServer` never returns
 * them, so no agent in the estate uses them. That is the point. Discovery
 * inventories the servers a platform exposes, not only the ones currently in
 * use, so drilling into "12 MCP servers" is expected to surface servers with no
 * agent behind them. Adding a business unit whose purpose mix covers their
 * technology is what makes one reachable.
 */
export const MCP_SERVER_CATALOG: McpServerDefinition[] = [
  {
    id: "mcp-sharepoint-kyc",
    name: "SharePoint MCP – Client Onboarding & KYC Documents",
    technology: "SharePoint",
    businessUnits: ["HR", "CUSTOMER_SUPPORT"],
    capabilities: [
      "Search and read client documents",
      "Apply sensitivity labels",
      "Share files with reviewers",
    ],
    tools: [
      { name: "findFileOrFolder", classification: "Read", dataAccess: "Documents" },
      { name: "readSmallTextFile", classification: "Read", dataAccess: "Documents" },
      { name: "readSmallBinaryFile", classification: "Read", dataAccess: "Documents" },
      {
        name: "getSensitivityLabels",
        classification: "Read",
        dataAccess: "Compliance metadata",
      },
      {
        name: "setSensitivityLabelOnFile",
        classification: "Write",
        dataAccess: "Compliance metadata",
      },
      { name: "shareFileOrFolder", classification: "Write", dataAccess: "Documents" },
      {
        name: "redactClientPii",
        classification: "Privileged",
        dataAccess: "Client documents",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-sharepoint-compliance",
    name: "SharePoint MCP – Engineering Compliance Archive",
    technology: "SharePoint",
    businessUnits: ["ENGINEERING"],
    capabilities: ["Browse SharePoint lists", "Track retention items"],
    tools: [
      { name: "listLists", classification: "Read", dataAccess: "Lists" },
      { name: "listListItems", classification: "Read", dataAccess: "Lists" },
      { name: "getListItem", classification: "Read", dataAccess: "Lists" },
      { name: "createListItem", classification: "Write", dataAccess: "Lists" },
      { name: "updateListItem", classification: "Write", dataAccess: "Lists" },
      {
        name: "flagRetentionException",
        classification: "Privileged",
        dataAccess: "Records retention",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-snowflake-markets",
    name: "Snowflake MCP – Markets & Trading Data Domain",
    technology: "Snowflake",
    businessUnits: ["FINANCE"],
    capabilities: ["Query trading and P&L tables", "Inspect query history"],
    tools: [
      { name: "run_query", classification: "Read", dataAccess: "Trading & P&L tables" },
      { name: "describe_table", classification: "Read", dataAccess: "Trading & P&L tables" },
      { name: "get_query_history", classification: "Read", dataAccess: "Query metadata" },
      { name: "get_table_sample", classification: "Read", dataAccess: "Trading & P&L tables" },
      {
        name: "getTradeSurveillanceAlerts",
        classification: "Privileged",
        dataAccess: "Trade surveillance system",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-snowflake-risk",
    name: "Snowflake MCP – Risk & Quant Data Domain",
    technology: "Snowflake",
    businessUnits: ["ANALYTICS"],
    capabilities: ["Query risk models", "Trace column lineage"],
    tools: [
      { name: "run_query", classification: "Read", dataAccess: "Risk & exposure tables" },
      { name: "list_views", classification: "Read", dataAccess: "Risk models" },
      { name: "get_lineage", classification: "Read", dataAccess: "Data lineage" },
      {
        name: "mask_column",
        classification: "Privileged",
        dataAccess: "Sensitive risk fields",
      },
      {
        name: "computeVarExposure",
        classification: "Privileged",
        dataAccess: "Value-at-risk engine",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-snowflake-warehouse",
    name: "Snowflake MCP – Enterprise Data Warehouse",
    technology: "Snowflake",
    businessUnits: [],
    capabilities: ["Query governed warehouse tables", "Manage warehouse access"],
    tools: [
      { name: "run_query", classification: "Read", dataAccess: "Governed warehouse" },
      { name: "list_databases", classification: "Read", dataAccess: "Warehouse catalog" },
      { name: "list_warehouses", classification: "Read", dataAccess: "Compute resources" },
      { name: "grant_privilege", classification: "Privileged", dataAccess: "Access grants" },
      { name: "revoke_privilege", classification: "Privileged", dataAccess: "Access grants" },
    ],
  },
  {
    id: "mcp-databricks-trading-systems",
    name: "Databricks MCP – Trading Systems Platform",
    technology: "Databricks",
    businessUnits: ["ENGINEERING"],
    capabilities: ["Run and monitor trading platform jobs"],
    tools: [
      { name: "list_clusters", classification: "Read", dataAccess: "Compute clusters" },
      {
        name: "run_notebook",
        classification: "Write",
        dataAccess: "Trading systems notebooks",
      },
      { name: "create_job", classification: "Write", dataAccess: "Job definitions" },
      { name: "get_job_run", classification: "Read", dataAccess: "Job run history" },
      {
        name: "deployTradingAlgoCanary",
        classification: "Privileged",
        dataAccess: "Trading algorithm deployment",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-databricks-risk-models",
    name: "Databricks MCP – Markets Risk Models",
    technology: "Databricks",
    businessUnits: ["FINANCE"],
    capabilities: ["Query lakehouse risk tables", "Manage pricing models"],
    tools: [
      { name: "query_table", classification: "Read", dataAccess: "Lakehouse risk tables" },
      { name: "run_job", classification: "Privileged", dataAccess: "Model batch jobs" },
      { name: "list_experiments", classification: "Read", dataAccess: "Model experiments" },
      { name: "register_model", classification: "Write", dataAccess: "Model registry" },
      {
        name: "backtestPricingModel",
        classification: "Privileged",
        dataAccess: "Pricing model backtests",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-github-trading-platform",
    name: "GitHub MCP – Trading Platform Repositories",
    technology: "GitHub",
    businessUnits: ["ENGINEERING"],
    capabilities: ["Read repositories", "Open pull requests"],
    tools: [
      { name: "get_file_contents", classification: "Read", dataAccess: "Source code" },
      { name: "list_branches", classification: "Read", dataAccess: "Source code" },
      { name: "list_issues", classification: "Read", dataAccess: "Issue tracker" },
      { name: "create_pull_request", classification: "Write", dataAccess: "Source code" },
      {
        name: "delete_branch",
        classification: "Destructive",
        dataAccess: "Repository administration",
      },
    ],
  },
  {
    id: "mcp-kb-hr-benefits",
    name: "Internal Knowledge Base MCP – HR & Benefits Handbook",
    technology: "Internal Knowledge Base",
    businessUnits: ["HR"],
    capabilities: ["Search HR policy articles"],
    tools: [
      { name: "search_articles", classification: "Read", dataAccess: "HR handbook" },
      { name: "get_article", classification: "Read", dataAccess: "HR handbook" },
      { name: "list_categories", classification: "Read", dataAccess: "HR handbook" },
    ],
  },
  {
    id: "mcp-kb-private-banking",
    name: "Internal Knowledge Base MCP – Private Banking Service Desk",
    technology: "Internal Knowledge Base",
    businessUnits: ["CUSTOMER_SUPPORT"],
    capabilities: ["Search service articles", "Review case history"],
    tools: [
      {
        name: "search_articles",
        classification: "Read",
        dataAccess: "Service knowledge base",
      },
      {
        name: "get_case_history",
        classification: "Read",
        dataAccess: "Client case history",
      },
      { name: "escalate_case", classification: "Write", dataAccess: "Case management" },
      {
        name: "summarizeClientInteraction",
        classification: "Privileged",
        dataAccess: "Client interaction transcripts",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-kb-research",
    name: "Internal Knowledge Base MCP – Equity & Credit Research Library",
    technology: "Internal Knowledge Base",
    businessUnits: ["ANALYTICS"],
    capabilities: ["Search research publications"],
    tools: [
      { name: "search_articles", classification: "Read", dataAccess: "Research library" },
      {
        name: "list_recent_publications",
        classification: "Read",
        dataAccess: "Research library",
      },
      {
        name: "flagMarketSensitiveContent",
        classification: "Privileged",
        dataAccess: "Market-sensitive research",
        custom: true,
      },
    ],
  },
  {
    id: "mcp-m365-rm-workspace",
    name: "Microsoft 365 MCP – Relationship Manager Workspace",
    technology: "Microsoft 365",
    businessUnits: ["CUSTOMER_SUPPORT"],
    capabilities: ["Search mail", "Draft and share documents"],
    tools: [
      { name: "search_mail", classification: "Read", dataAccess: "Mail and calendar" },
      { name: "share_document", classification: "Write", dataAccess: "Workspace content" },
      {
        name: "draftClientCorrespondence",
        classification: "Privileged",
        dataAccess: "Client correspondence",
        custom: true,
      },
    ],
  },
];

/**
 * Registered tools across the whole catalog, counted as server-scoped tool
 * instances rather than distinct tool names: the same tool name on two servers
 * is two separately registered tools.
 *
 * Exported so the stat card, the relationship graph's legend and the discovery
 * fixtures all read one number instead of each writing their own `reduce`.
 */
export const MCP_TOOL_COUNT = MCP_SERVER_CATALOG.reduce(
  (count, server) => count + server.tools.length,
  0,
);

/** Discovered servers first, then the manually connected ones - never twice. */
export const withConnected = (
  discovered: McpServerDefinition[],
  connected: McpServerDefinition[],
): McpServerDefinition[] => [
  ...discovered,
  ...connected.filter(server => !discovered.some(({ id }) => id === server.id)),
];

/**
 * The catalog the active profile discovers. The Investment Banking profile
 * adds its customer-support servers; every other estate reads the shared
 * catalog unchanged.
 */
export const getMcpServerCatalog = (): McpServerDefinition[] =>
  isInvestmentBankingDemo()
    ? [...MCP_SERVER_CATALOG, ...DEMO_MCP_SERVER_CATALOG]
    : MCP_SERVER_CATALOG;

export const getMcpToolCount = (): number =>
  getMcpServerCatalog().reduce(
    (count, server) => count + server.tools.length,
    0,
  );

/** Intent Policies with an enforcement mapping, for the active profile. */
export const getIntentPolicies = (): IntentPolicyDefinition[] =>
  isInvestmentBankingDemo() ? [CUSTOMER_SUPPORT_DATA_ACCESS] : [];

/**
 * The evidence behind each purpose classification, shown on the agent detail
 * page. Per-agent `metadata` rows are composed by the store; everything here is
 * shared by every agent of that purpose.
 */
export const CLASSIFICATION_EVIDENCE_CATALOG: Record<
  AgentPurpose,
  Omit<AgentClassificationEvidence, "metadata">
> = {
  ENGINEERING: {
    systemPromptExcerpt:
      "You are a senior engineering assistant. Review open pull requests, suggest fixes, and update branches when the change is low risk.",
    registeredTools: ["get_file_contents", "create_pull_request", "create_job"],
    dataSource: "Source repositories and build metadata",
    applicationTags: ["engineering", "developer-tooling", "ci"],
  },
  ANALYTICS: {
    systemPromptExcerpt:
      "You answer business questions from the governed warehouse. Always cite the table and column you used.",
    registeredTools: ["run_query", "list_views"],
    dataSource: "Governed warehouse tables",
    applicationTags: ["analytics", "self-service-bi"],
  },
  RESEARCH: {
    systemPromptExcerpt:
      "You synthesise market and competitor material into briefing notes for the strategy team.",
    registeredTools: ["search_articles", "list_recent_publications"],
    dataSource: "Internal research library",
    applicationTags: ["research", "strategy"],
  },
  HR: {
    systemPromptExcerpt:
      "You help employees with policy, benefits and leave questions. Use the HR handbook as the source of truth.",
    registeredTools: ["findFileOrFolder", "shareFileOrFolder"],
    dataSource: "HR document library",
    applicationTags: ["hr", "employee-experience"],
  },
  FINANCE: {
    systemPromptExcerpt:
      "You assist the finance team with close reporting and variance analysis against the ledger.",
    registeredTools: ["run_query", "query_table"],
    dataSource: "Financial reporting schema",
    applicationTags: ["finance", "reporting"],
  },
  CUSTOMER_SUPPORT: {
    systemPromptExcerpt:
      "You draft replies to customer tickets using the support knowledge base and recent case history.",
    registeredTools: ["search_articles", "get_case_history"],
    dataSource: "Support knowledge base and case history",
    applicationTags: ["support", "customer-facing"],
  },
  CONTENT_CREATION: {
    systemPromptExcerpt:
      "You draft proposals and internal communications from approved templates and workspace content.",
    registeredTools: ["search_mail", "share_document"],
    dataSource: "Microsoft 365 workspace content",
    applicationTags: ["content", "collateral"],
  },
};

/* --------------------------------------------------------- recommendations */

/**
 * A recommendation plus the selector that decides which agents it applies to.
 * `selector` is mock-only bookkeeping and is stripped before the entry leaves
 * the store - the real engine will score agents server-side.
 */
export interface RecommendationCatalogEntry
  extends Omit<AgentPolicyRecommendation, "appliesTo"> {
  demoScenario?: "customer-support" | "churn-analytics";
  selector: {
    purposes: AgentPurpose[];
    technologies: AgentTechnology[];
    /** Named agents (`GeneratedAgent.demoKey`); when set, it alone decides. */
    agentKeys?: string[];
  };
}

const allow = (label: string): PolicyControl => ({ sense: "ALLOW", label });
const block = (label: string): PolicyControl => ({ sense: "BLOCK", label });

/**
 * Names describe the outcome; `outcome` says what happens; `whyRecommended`
 * says why this agent; `enforcement` shows how it becomes a real control.
 *
 * No entry selects RESEARCH - that gap is deliberate and is what the Custom
 * Policy tab exists for.
 */
export const RECOMMENDATION_CATALOG: RecommendationCatalogEntry[] = [
  {
    id: "rec-github-destructive",
    name: "Prevent destructive repository actions",
    outcome:
      "Agents keep reading code and opening pull requests, but can no longer delete branches, force-push or rewrite history.",
    whyRecommended:
      "These agents are classified as Engineering agents and require code read/write access, but repository deletion is outside their expected purpose.",
    controls: [
      allow("Read repository contents, issues and pull requests"),
      allow("Create and update files on a working branch"),
      block("Delete branches, tags or repositories"),
      block("Force-push or rewrite commit history"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "TOOL_ACTION_CONTROLS",
    technology: "GitHub",
    frameworks: ["NIST", "SOC 2"],
    enforcement: {
      resource: "GitHub MCP Server",
      toolGroups: ["Repository management"],
      allowed: ["get_file_contents", "list_issues", "create_pull_request"],
      blocked: ["delete_repository", "delete_branch", "force_push"],
      enforcementLayer: "MCP tool policy",
    },
    selector: { purposes: ["ENGINEERING"], technologies: ["GitHub"] },
  },
  {
    id: "rec-github-scope",
    name: "Limit repository scope",
    outcome:
      "Agents reach only the repositories their team owns, instead of every repository the connection can see.",
    whyRecommended:
      "The connected GitHub App grants organization-wide read access, which is broader than these agents' engineering purpose requires.",
    controls: [
      allow("Read repositories owned by the agent's own team"),
      block("Read or write repositories outside the owning team"),
      block("Enumerate organization-wide repository listings"),
    ],
    strength: "RECOMMENDED",
    category: "DATA_ACCESS_CONTROLS",
    technology: "GitHub",
    frameworks: ["NIST"],
    enforcement: {
      resource: "GitHub MCP Server",
      toolGroups: ["Repository discovery", "Repository read"],
      allowed: ["get_file_contents", "list_branches"],
      blocked: ["list_org_repositories", "search_code_org_wide"],
      enforcementLayer: "MCP tool policy",
    },
    selector: { purposes: ["ENGINEERING"], technologies: ["GitHub"] },
  },
  {
    id: "rec-eng-admin-tools",
    name: "Restrict administrative tool access",
    outcome:
      "Agents keep querying and building, but privileged administrative operations require a human operator.",
    whyRecommended:
      "These Engineering agents hold credentials that expose job-control and permission-administration tools they never need for code and data work.",
    controls: [
      allow("Query tables and read job history"),
      allow("Trigger builds on non-production targets"),
      block("Grant, revoke or modify permissions"),
      block("Delete or reconfigure production compute"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "TOOL_ACTION_CONTROLS",
    technology: "Databricks",
    frameworks: ["NIST", "ISO"],
    enforcement: {
      resource: "Databricks workspace",
      toolGroups: ["Job control", "Permission administration"],
      allowed: ["query_table", "list_jobs"],
      blocked: ["run_job", "update_permissions", "delete_cluster"],
      enforcementLayer: "Resource policy",
    },
    selector: {
      purposes: ["ENGINEERING"],
      technologies: ["Databricks", "SharePoint"],
    },
  },
  {
    id: "rec-hr-file-sharing",
    name: "Prevent sensitive file sharing",
    outcome:
      "Agents can find and summarise HR documents, but cannot share them outward or widen their permissions.",
    whyRecommended:
      "These agents are classified as HR agents reading an employee document library, and the connected tools include outbound sharing.",
    controls: [
      allow("Search and summarise HR documents"),
      block("Share documents with external recipients"),
      block("Change document or folder permissions"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "PRIVACY_PROTECTION",
    technology: "SharePoint",
    frameworks: ["GDPR", "ISO"],
    enforcement: {
      resource: "SharePoint MCP Server",
      toolGroups: ["Document sharing"],
      allowed: ["findFileOrFolder"],
      blocked: ["shareFileOrFolder", "update_permissions"],
      enforcementLayer: "MCP tool policy",
    },
    selector: { purposes: ["HR"], technologies: ["SharePoint"] },
  },
  {
    id: "rec-hr-data-export",
    name: "Restrict employee data export",
    outcome:
      "Agents answer individual employee questions, but cannot pull rosters or bulk employee records out of the system.",
    whyRecommended:
      "These HR agents can reach whole-population employee data, while their purpose only requires answering one employee's question at a time.",
    controls: [
      allow("Retrieve records for a single named employee"),
      block("Export employee rosters or bulk record sets"),
      block("Write employee records to an external destination"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "REGULATORY_COMPLIANCE",
    technology: "Internal Knowledge Base",
    frameworks: ["GDPR", "SOC 2"],
    enforcement: {
      resource: "HR knowledge base",
      toolGroups: ["Bulk export", "Record retrieval"],
      allowed: ["search_articles"],
      blocked: ["bulk_export", "write_extract"],
      enforcementLayer: "Data access policy",
    },
    selector: {
      purposes: ["HR"],
      technologies: ["SharePoint", "Internal Knowledge Base"],
    },
  },
  {
    id: "rec-hr-redact",
    name: "Redact privacy-sensitive content",
    outcome:
      "Personal identifiers are masked in agent responses while the surrounding answer stays intact.",
    whyRecommended:
      "The documents these HR agents read carry national identifiers and compensation figures that their answers do not need to repeat.",
    controls: [
      allow("Answer using redacted document content"),
      block("Return national identifiers or bank details verbatim"),
      block("Return compensation figures for a named individual"),
    ],
    strength: "RECOMMENDED",
    category: "PRIVACY_PROTECTION",
    technology: "Internal Knowledge Base",
    frameworks: ["GDPR"],
    enforcement: {
      resource: "HR document library",
      toolGroups: ["Response filtering"],
      allowed: ["findFileOrFolder", "search_articles"],
      blocked: ["return_unmasked_pii"],
      enforcementLayer: "Response policy",
    },
    selector: {
      purposes: ["HR"],
      technologies: ["SharePoint", "Internal Knowledge Base"],
    },
  },
  {
    id: "rec-analytics-readonly",
    name: "Read-only data access",
    outcome:
      "Agents keep answering questions from the warehouse, but can no longer write, update or drop anything.",
    whyRecommended:
      "These agents are classified as Analytics agents whose purpose is reporting, yet their credentials carry write and DDL privileges.",
    controls: [
      allow("Run read queries against governed tables"),
      block("Insert, update or delete rows"),
      block("Create, alter or drop tables and schemas"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "BUSINESS_RULES",
    technology: "Snowflake",
    frameworks: ["NIST", "ISO"],
    enforcement: {
      resource: "Snowflake MCP Server",
      toolGroups: ["Query execution", "Schema management"],
      allowed: ["run_query"],
      blocked: ["write_extract", "create_table", "drop_table"],
      enforcementLayer: "Resource policy",
    },
    selector: {
      purposes: ["ANALYTICS"],
      technologies: ["Snowflake", "Databricks"],
    },
  },
  {
    id: "rec-analytics-bulk-export",
    name: "Prevent bulk data export",
    outcome:
      "Agents return the rows needed to answer a question, and stop short of extracting whole tables.",
    whyRecommended:
      "Analytics agents on this warehouse can return unbounded result sets, which turns a reporting question into a data extract.",
    controls: [
      allow("Return aggregated and sampled results"),
      block("Return result sets above the approved row limit"),
      block("Write query output to external storage"),
    ],
    strength: "RECOMMENDED",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Snowflake",
    frameworks: ["ISO"],
    enforcement: {
      resource: "Snowflake MCP Server",
      toolGroups: ["Result delivery"],
      allowed: ["run_query"],
      blocked: ["unload_to_stage", "write_extract"],
      enforcementLayer: "Resource policy",
    },
    selector: {
      purposes: ["ANALYTICS"],
      technologies: ["Snowflake", "Databricks"],
    },
  },
  {
    id: "rec-analytics-datasets",
    name: "Restrict access to approved datasets",
    outcome:
      "Agents query only the datasets their team has been approved for, rather than everything the warehouse role can see. Finance schemas stay reachable where the agent's registered purpose covers finance reporting, such as churn and risk analytics.",
    whyRecommended:
      "The warehouse role behind these Analytics agents reaches HR and other restricted schemas that their reporting purpose does not cover, even though finance schemas remain in scope for the reporting these agents are provisioned to do.",
    controls: [
      allow("Query datasets on the team's approved list, including provisioned finance schemas"),
      block("Query HR or other restricted schemas outside the approved list"),
    ],
    strength: "RECOMMENDED",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Snowflake",
    frameworks: ["SOC 2"],
    enforcement: {
      resource: "Snowflake MCP Server",
      toolGroups: ["Dataset scope"],
      allowed: ["run_query"],
      blocked: ["query_restricted_schema"],
      enforcementLayer: "Data access policy",
    },
    selector: { purposes: ["ANALYTICS"], technologies: ["Snowflake"] },
  },
  {
    id: "rec-finance-material",
    name: "Mask material financial figures before close",
    outcome:
      "Agents keep producing variance commentary, with unreleased figures masked until results are published.",
    whyRecommended:
      "These Finance agents read the reporting schema during the close window, when the figures are material and not yet released.",
    controls: [
      allow("Return published period results"),
      allow("Return variance commentary on masked figures"),
      block("Return unreleased period figures before publication"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "REGULATORY_COMPLIANCE",
    technology: "Snowflake",
    frameworks: ["SOX", "SOC 2"],
    enforcement: {
      resource: "Snowflake MCP Server",
      toolGroups: ["Financial reporting"],
      allowed: ["run_query"],
      blocked: ["query_unreleased_period"],
      enforcementLayer: "Column masking policy",
    },
    selector: { purposes: ["FINANCE"], technologies: ["Snowflake"] },
  },
  {
    id: "rec-finance-payments",
    name: "Prevent agents initiating payments",
    outcome:
      "Agents can analyse and prepare, but a person still has to submit any payment or journal entry.",
    whyRecommended:
      "These Finance agents hold tooling that can post journal entries and release payments - actions outside an analysis purpose.",
    controls: [
      allow("Prepare draft journal entries for review"),
      block("Post journal entries to the ledger"),
      block("Release or approve payments"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "TOOL_ACTION_CONTROLS",
    technology: "Databricks",
    frameworks: ["SOX"],
    enforcement: {
      resource: "Finance data platform",
      toolGroups: ["Ledger posting", "Payment release"],
      allowed: ["query_table"],
      blocked: ["post_journal_entry", "release_payment"],
      enforcementLayer: "Resource policy",
    },
    selector: {
      purposes: ["FINANCE"],
      technologies: ["Databricks", "Snowflake"],
    },
  },
  {
    id: "rec-support-customer-pii",
    name: "Redact customer identifiers in replies",
    outcome:
      "Support drafts keep their detail, with customer identifiers masked before the reply leaves the agent.",
    whyRecommended:
      "These Customer Support agents read case history containing payment and identity details that a reply never needs to repeat.",
    controls: [
      allow("Draft replies using redacted case history"),
      block("Return full card or account numbers"),
      block("Return national identifiers or dates of birth"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "PRIVACY_PROTECTION",
    technology: "Internal Knowledge Base",
    frameworks: ["GDPR", "PCI DSS"],
    enforcement: {
      resource: "Support knowledge base",
      toolGroups: ["Response filtering"],
      allowed: ["search_articles", "run_query"],
      blocked: ["return_unmasked_pii"],
      enforcementLayer: "Response policy",
    },
    selector: {
      purposes: ["CUSTOMER_SUPPORT"],
      technologies: ["Internal Knowledge Base", "Snowflake"],
    },
  },
  {
    id: "rec-support-finance-scope",
    name: "Restrict finance data access by agent purpose",
    outcome:
      "Customer Support agents keep answering account and service questions from the support knowledge base, but can no longer query finance-classified balance, AUM or trading data - that access stays with agents whose registered purpose is Finance or Analytics reporting.",
    whyRecommended:
      "These agents are classified as Customer Support agents: their registered purpose is resolving service requests, not financial reporting, so the finance-classified data that Analytics and Finance agents are provisioned to see falls outside their intent.",
    controls: [
      allow("Search and summarise support case history"),
      block("Query finance-classified balance, AUM or trading data"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Internal Knowledge Base",
    frameworks: ["SOC 2", "NIST"],
    enforcement: {
      resource: "Support knowledge base",
      toolGroups: ["Finance schema access"],
      allowed: ["search_articles", "get_case_history"],
      blocked: ["query_finance_schema"],
      enforcementLayer: "Data access policy",
    },
    selector: {
      purposes: ["CUSTOMER_SUPPORT"],
      technologies: ["Internal Knowledge Base"],
    },
  },
  {
    id: "rec-m365-oversharing",
    name: "Prevent oversharing from Microsoft 365",
    outcome:
      "Agents keep drafting from workspace content, but cannot widen who can see a document or send it outside the tenant.",
    whyRecommended:
      "These Content Creation agents draft from Microsoft 365 content and hold sharing tools that can publish outside the organization.",
    controls: [
      allow("Read and draft from workspace content"),
      block("Share content with external or anonymous links"),
      block("Send workspace content to external recipients"),
    ],
    strength: "STRONGLY_RECOMMENDED",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Microsoft 365",
    frameworks: ["ISO", "SOC 2"],
    enforcement: {
      resource: "Microsoft 365 MCP Server",
      toolGroups: ["Content sharing"],
      allowed: ["search_mail", "share_document"],
      blocked: ["share_document_externally", "create_anonymous_link"],
      enforcementLayer: "MCP tool policy",
    },
    selector: {
      purposes: ["CONTENT_CREATION"],
      technologies: ["Microsoft 365"],
    },
  },
];

/* ---------------------------------------------------------- policy catalog */

/**
 * Everything the All Policies tab offers. The recommendation catalog is
 * projected into this shape by `buildPolicyCatalog`, plus broader baselines
 * that are never auto-recommended - so applying from either surface resolves
 * through the same id space.
 */
const BASELINE_POLICIES: PolicyCatalogItem[] = [
  {
    id: "pol-enterprise-baseline",
    name: "Enterprise Access Control Baseline",
    category: "BUSINESS_RULES",
    description:
      "Applies the organization's baseline access controls to every agent-resource interaction.",
    technology: "Any",
    scope: "All agents in the environment",
    frameworks: ["NIST", "ISO"],
    controls: [
      allow("Access resources on the approved list"),
      block("Reach unmanaged or unregistered resources"),
    ],
  },
  {
    id: "pol-data-residency",
    name: "EU Data Residency",
    category: "REGULATORY_COMPLIANCE",
    description:
      "Keep regulated workloads within approved EU locations.",
    technology: "Any",
    scope: "Agents reaching EU-regulated data sources",
    frameworks: ["GDPR"],
    controls: [
      allow("Read data held in the agent's own region"),
      block("Move or copy data across regional boundaries"),
    ],
  },
  {
    id: "pol-operating-hours",
    name: "Approved Operating Hours",
    category: "BUSINESS_RULES",
    description:
      "Limits agent activity to the hours the business has approved for automated access.",
    technology: "Any",
    scope: "Agents with unattended or scheduled execution",
    frameworks: ["ISO"],
    controls: [
      allow("Operate during approved business hours"),
      block("Access protected resources outside approved hours"),
    ],
  },
  {
    id: "pol-activity-evidence",
    name: "Model and Prompt Activity Logging",
    category: "BUSINESS_RULES",
    description:
      "Records the prompt, model and resource for every protected-resource access an agent makes.",
    technology: "Any",
    scope: "All agents in the environment",
    frameworks: ["SOC 2", "ISO"],
    controls: [
      allow("Access protected resources with activity recorded"),
      block("Access protected resources when logging is unavailable"),
    ],
  },
  {
    id: "pol-cross-tenant",
    name: "Cross-Tenant Sharing Restriction",
    category: "DATA_ACCESS_CONTROLS",
    description:
      "Stops workspace content reaching recipients outside the organization's tenant.",
    technology: "Microsoft 365",
    scope: "Agents with Microsoft 365 sharing capabilities",
    frameworks: ["ISO"],
    controls: [
      allow("Share content inside the tenant"),
      block("Share content with guest or external identities"),
    ],
  },
];

/**
 * What a catalog policy needs to be applicable. Mock-only bookkeeping: the real
 * engine will decide applicability server-side. A policy with no entry here
 * applies wherever its technology matches.
 */
export interface PolicyRequirementRule {
  /** Data sources that satisfy the requirement. */
  dataSourceIds: string[];
  required: string;
  reason: string;
}

/** Data sources held under EU residency obligations (demo snapshot ids). */
const EU_REGULATED_SOURCE_IDS = ["8a4882ed9feee90a019fefd8fe570bb7"];

export const POLICY_REQUIREMENTS: Record<string, PolicyRequirementRule> = {
  "pol-data-residency": {
    dataSourceIds: EU_REGULATED_SOURCE_IDS,
    required: "EU-regulated data source",
    reason:
      "No EU-regulated data resources were found in the selected agents' current access path.",
  },
};

const scopeSentence = (entry: RecommendationCatalogEntry): string => {
  const purposes = entry.selector.purposes
    .map(purpose => PURPOSE_LABELS[purpose])
    .join(" and ");
  return `${purposes} agents reaching ${entry.selector.technologies.join(", ")}`;
};

export const POLICY_CATALOG: PolicyCatalogItem[] = [
  ...RECOMMENDATION_CATALOG.map(entry => ({
    id: entry.id,
    name: entry.name,
    category: entry.category,
    description: entry.outcome,
    technology: entry.technology,
    scope: scopeSentence(entry),
    frameworks: entry.frameworks,
    controls: entry.controls,
  })),
  ...BASELINE_POLICIES,
];

/* ------------------------------------------------------------ draft policy */

/**
 * Keyword rules the Custom Policy tab's mock draft generator runs over the
 * administrator's intent. Deliberately simple and inspectable: each rule that
 * fires contributes exactly one control and names the phrase it came from.
 */
export interface DraftPolicyKeywordRule {
  match: RegExp;
  sense: PolicyControlSense;
  label: string;
  category: PolicyCategory;
  technology?: AgentTechnology;
}

export const DRAFT_POLICY_KEYWORDS: DraftPolicyKeywordRule[] = [
  {
    match: /\b(delet\w*|destroy\w*|destructive|drop|remov\w*)\b/,
    sense: "BLOCK",
    label: "Deny delete and destructive operations",
    category: "TOOL_ACTION_CONTROLS",
  },
  {
    match: /\b(admin\w*|permission\w*|grant\w*|revok\w*)\b/,
    sense: "BLOCK",
    label: "Deny permission administration",
    category: "TOOL_ACTION_CONTROLS",
  },
  {
    match: /\b(read|view|query|search)\b/,
    sense: "ALLOW",
    label: "Allow read access to approved resources",
    category: "DATA_ACCESS_CONTROLS",
  },
  {
    match: /\b(write|update|create|modify|edit)\b/,
    sense: "ALLOW",
    label: "Allow create and update on approved resources",
    category: "DATA_ACCESS_CONTROLS",
  },
  {
    match: /\b(export|download|extract|bulk)\b/,
    sense: "BLOCK",
    label: "Deny bulk export and extraction",
    category: "DATA_ACCESS_CONTROLS",
  },
  {
    match: /\b(share|send|email|forward|external)\b/,
    sense: "BLOCK",
    label: "Deny outbound sharing to external recipients",
    category: "DATA_ACCESS_CONTROLS",
  },
  {
    match: /\b(pii|personal|ssn|social security|identifier)\b/,
    sense: "BLOCK",
    label: "Deny returning personal identifiers",
    category: "PRIVACY_PROTECTION",
  },
  {
    match: /\b(salary|compensation|payroll)\b/,
    sense: "BLOCK",
    label: "Deny returning compensation figures",
    category: "PRIVACY_PROTECTION",
  },
  {
    match: /\b(mask|redact|anonymi[sz]e)\b/,
    sense: "ALLOW",
    label: "Allow responses built from masked content",
    category: "PRIVACY_PROTECTION",
  },
  {
    match: /\b(medical|health|hipaa|patient)\b/,
    sense: "BLOCK",
    label: "Deny access to health information",
    category: "REGULATORY_COMPLIANCE",
  },
  {
    match: /\b(production|prod)\b/,
    sense: "BLOCK",
    label: "Deny changes to production resources",
    category: "BUSINESS_RULES",
  },
  {
    match: /\bread.?only\b/,
    sense: "BLOCK",
    label: "Deny every write and administrative operation",
    category: "BUSINESS_RULES",
  },
  {
    match: /\b(approve|approval|human|review)\b/,
    sense: "BLOCK",
    label: "Require human approval before the action completes",
    category: "BUSINESS_RULES",
  },
  {
    match: /\b(log|audit|evidence)\b/,
    sense: "ALLOW",
    label: "Allow access with activity recorded",
    category: "BUSINESS_RULES",
  },
  {
    match: /\b(github|repo|repository|branch)\b/,
    sense: "ALLOW",
    label: "Allow repository read and pull-request activity",
    category: "TOOL_ACTION_CONTROLS",
    technology: "GitHub",
  },
  {
    match: /\bsnowflake\b/,
    sense: "ALLOW",
    label: "Allow governed Snowflake query execution",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Snowflake",
  },
  {
    match: /\bdatabricks\b/,
    sense: "ALLOW",
    label: "Allow governed Databricks table access",
    category: "DATA_ACCESS_CONTROLS",
    technology: "Databricks",
  },
  {
    match: /\bsharepoint\b/,
    sense: "ALLOW",
    label: "Allow SharePoint document search",
    category: "DATA_ACCESS_CONTROLS",
    technology: "SharePoint",
  },
];

export const SECURITY_STATUS_ORDER: AgentSecurityStatus[] = [
  "SECURE",
  "NOT_SECURE",
  "NOT_EVALUATED",
];

/* ----------------------------------------------------------- agent mission */

/** The governed name a purpose reads as on the Mission card. */
export const MISSION_NAMES: Record<AgentPurpose, string> = {
  ENGINEERING: "Engineering Delivery Assistant",
  ANALYTICS: "Self-Service Analytics Assistant",
  RESEARCH: "Research Synthesis Assistant",
  HR: "HR Benefits Assistant",
  FINANCE: "Finance Reporting Assistant",
  CUSTOMER_SUPPORT: "Customer Support Assistant",
  CONTENT_CREATION: "Content Drafting Assistant",
};

/**
 * Mission evidence per purpose. Deliberately reuses the same registered tools
 * and data source as `CLASSIFICATION_EVIDENCE_CATALOG`, so the Mission card and
 * the "Why TrustLogix classified this agent this way" card can never contradict
 * each other on the same screen.
 */
export const MISSION_EVIDENCE_CATALOG: Record<
  AgentPurpose,
  Array<Omit<AgentMissionEvidence, "id" | "observedAt">>
> = {
  ENGINEERING: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Reviews pull requests and suggests fixes",
      description:
        "The system prompt scopes the agent to reviewing open pull requests and proposing low-risk changes.",
      signalCount: 3,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Repository read and write tools registered",
      description:
        "get_file_contents and create_pull_request are registered, so the agent both reads and proposes changes to source.",
      signalCount: 2,
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Reaches source repositories and build metadata",
      description:
        "Observed access is limited to repository contents and build metadata - no customer or employee data.",
    },
  ],
  ANALYTICS: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Answers business questions from the warehouse",
      description:
        "The prompt requires every answer to cite the table and column it came from.",
      signalCount: 2,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Read-only query tools registered",
      description:
        "run_query and query_table are registered; no write or export tool is present.",
      signalCount: 2,
    },
    {
      type: "DATA",
      source: "Semantic Model",
      title: "Scoped to governed warehouse tables",
      description:
        "All observed reads resolve against the governed warehouse, not raw landing tables.",
    },
  ],
  RESEARCH: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Synthesises market and competitor material",
      description:
        "The prompt describes briefing-note synthesis for the strategy team.",
      signalCount: 2,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Single search tool registered",
      description:
        "Only search_articles is registered, which is too narrow to separate research from general retrieval.",
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Internal research library only",
      description:
        "Observed access is limited to the internal research library.",
      stale: true,
    },
  ],
  HR: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Answers policy, benefits and leave questions",
      description:
        "The prompt names the HR handbook as the source of truth for employee questions.",
      signalCount: 4,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Document search and share tools registered",
      description:
        "search_files and share_file are registered, so the agent can both read and distribute HR documents.",
      signalCount: 2,
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Reaches the HR document library",
      description:
        "Observed reads resolve against the HR document library, which carries employee personal data.",
    },
    {
      type: "IDENTITY",
      source: "Identity Provider",
      title: "Service identity is owned by People Operations",
      description:
        "The registered service principal sits in the People Operations group.",
    },
  ],
  FINANCE: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Assists with close reporting and variance analysis",
      description:
        "The prompt scopes the agent to financial close reporting against the ledger.",
      signalCount: 3,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Query and extract tools registered",
      description:
        "run_query and write_extract are registered - the extract tool can move financial records out of the warehouse.",
      signalCount: 2,
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Reaches the financial reporting schema",
      description:
        "Observed access covers the financial reporting schema, including regulated ledger tables.",
    },
  ],
  CUSTOMER_SUPPORT: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Drafts replies to customer tickets",
      description:
        "The prompt scopes the agent to ticket replies drawn from the knowledge base and case history.",
      signalCount: 3,
    },
    {
      type: "TOOL",
      source: "Tool Definitions",
      title: "Knowledge and case-history tools registered",
      description:
        "search_articles and run_query are registered, giving the agent both article and case access.",
      signalCount: 2,
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Reaches support knowledge and case history",
      description:
        "Observed reads cover the support knowledge base and recent customer cases.",
    },
  ],
  CONTENT_CREATION: [
    {
      type: "SEMANTIC",
      source: "System Prompt",
      title: "Drafts proposals and internal communications",
      description:
        "The prompt scopes the agent to drafting from approved templates and workspace content.",
      signalCount: 2,
    },
    {
      type: "MCP",
      source: "MCP Registration",
      title: "Registered against the workspace context service",
      description:
        "The agent is registered to a Microsoft 365 context service exposing mail and document tools.",
    },
    {
      type: "DATA",
      source: "Data Access",
      title: "Reaches Microsoft 365 workspace content",
      description:
        "Observed access covers workspace documents and mail, which can contain confidential material.",
    },
  ],
};

/**
 * Only used under NEEDS_MORE_CONTEXT: plausible readings, none confident enough
 * to settle on. Listing them is the honest alternative to naming a mission the
 * assessment cannot support.
 */
export const MISSION_CANDIDATE_CATALOG: Record<
  AgentPurpose,
  AgentMissionCandidate[]
> = {
  ENGINEERING: [
    {
      name: "Code Review Assistant",
      confidence: 48,
      signals: ["Repository read tools", "Pull-request vocabulary"],
    },
    {
      name: "Build Triage Assistant",
      confidence: 34,
      signals: ["Build metadata access"],
    },
  ],
  ANALYTICS: [
    {
      name: "Reporting Copilot",
      confidence: 46,
      signals: ["Warehouse read tools"],
    },
    {
      name: "Metric Lineage Assistant",
      confidence: 31,
      signals: ["Semantic model reads"],
    },
  ],
  RESEARCH: [
    {
      name: "Market Research Assistant",
      confidence: 44,
      signals: ["Article search tool", "Research library access"],
    },
    {
      name: "General Knowledge Retrieval",
      confidence: 38,
      signals: ["Single generic search tool"],
    },
    {
      name: "Competitive Intelligence Digest",
      confidence: 29,
      signals: ["Briefing vocabulary in the prompt"],
    },
  ],
  HR: [
    {
      name: "Employee Policy Assistant",
      confidence: 47,
      signals: ["HR document access"],
    },
    {
      name: "Recruiting Screening Assistant",
      confidence: 30,
      signals: ["Candidate document reads"],
    },
  ],
  FINANCE: [
    {
      name: "Close Reporting Assistant",
      confidence: 45,
      signals: ["Ledger table reads"],
    },
    {
      name: "Spend Analysis Assistant",
      confidence: 33,
      signals: ["Vendor table reads"],
    },
  ],
  CUSTOMER_SUPPORT: [
    {
      name: "Case Deflection Assistant",
      confidence: 43,
      signals: ["Knowledge base reads"],
    },
    {
      name: "Support Knowledge Retrieval",
      confidence: 36,
      signals: ["Article search tool"],
    },
  ],
  CONTENT_CREATION: [
    {
      name: "Proposal Drafting Assistant",
      confidence: 42,
      signals: ["Template document reads"],
    },
    {
      name: "Internal Communications Assistant",
      confidence: 35,
      signals: ["Mail search tool"],
    },
  ],
};

/**
 * Only used under REVIEW_RECOMMENDED. Named after a tool on that technology's
 * `MCP_SERVER_CATALOG` servers, so "what changed" always names a tool that
 * plausibly exists on that resource.
 */
export const MISSION_CONTEXT_CHANGE_CATALOG: Record<
  AgentTechnology,
  Array<Omit<AgentMissionContextChange, "id">>
> = {
  SharePoint: [
    {
      name: "share_file",
      service: "SharePoint",
      classification: "New distribution capability",
    },
  ],
  Snowflake: [
    {
      name: "write_extract",
      service: "Snowflake",
      classification: "New export capability",
    },
  ],
  Databricks: [
    {
      name: "run_job",
      service: "Databricks",
      classification: "New privileged capability",
    },
  ],
  "Internal Knowledge Base": [
    {
      name: "search_articles",
      service: "Internal Knowledge Base",
      classification: "Broadened article scope",
    },
  ],
  GitHub: [
    {
      name: "create_pull_request",
      service: "GitHub",
      classification: "New write capability",
    },
    {
      name: "delete_branch",
      service: "GitHub",
      classification: "New destructive capability",
    },
  ],
  "Microsoft 365": [
    {
      name: "share_document",
      service: "Microsoft 365",
      classification: "New distribution capability",
    },
  ],
  ServiceNow: [
    {
      name: "updateIncident",
      service: "ServiceNow",
      classification: "New write capability",
    },
  ],
  Salesforce: [
    {
      name: "logInteraction",
      service: "Salesforce",
      classification: "New write capability",
    },
  ],
};

/** The paragraph that reconciles the evidence into the assessment, per status. */
export const MISSION_SYNTHESIS: Record<AgentMissionStatus, string> = {
  ADMINISTRATOR_DEFINED:
    "This mission was recorded by an administrator, so TrustLogix treats it as authoritative and uses it to scope policy recommendations.",
  INFERRED_NEEDS_REVIEW:
    "The system prompt, the registered tools and the observed data access all point at the same purpose. TrustLogix has not seen an administrator confirm it, so the mission is still an inference.",
  CONFIRMED:
    "An administrator reviewed the inferred mission against the evidence and confirmed it. TrustLogix now treats it as the governed intent for this agent.",
  OVERRIDDEN:
    "An administrator replaced the inferred mission. The prior assessment is kept in mission history so a later reviewer can see what changed and why.",
  NEEDS_MORE_CONTEXT:
    "The available signals support more than one reading and none of them clearly. TrustLogix would rather say so than name a mission the evidence cannot carry.",
  REVIEW_RECOMMENDED:
    "Capabilities appeared after this mission was last confirmed. The confirmed mission may no longer describe everything the agent can now reach.",
};

/** The broader mission offered after context drift, per purpose. */
export const MISSION_SUGGESTION_CATALOG: Partial<
  Record<AgentPurpose, Omit<AgentMissionSuggestion, "confidence">>
> = {
  ENGINEERING: {
    name: "Engineering Delivery and Release Assistant",
    description:
      "Reviews pull requests and now also triggers build and release jobs against source repositories.",
    purposeCategory: "ENGINEERING",
  },
  FINANCE: {
    name: "Finance Reporting and Extract Assistant",
    description:
      "Reports on the financial close and now also extracts ledger records out of the warehouse.",
    purposeCategory: "FINANCE",
  },
  HR: {
    name: "HR Benefits and Document Distribution Assistant",
    description:
      "Answers benefits questions and now also distributes HR documents to employees.",
    purposeCategory: "HR",
  },
  ANALYTICS: {
    name: "Analytics Reporting and Extract Assistant",
    description:
      "Answers governed reporting questions and now also writes extracts out of the warehouse.",
    purposeCategory: "ANALYTICS",
  },
  CUSTOMER_SUPPORT: {
    name: "Customer Support and Case Analysis Assistant",
    description:
      "Drafts ticket replies and now also queries case history directly.",
    purposeCategory: "CUSTOMER_SUPPORT",
  },
  CONTENT_CREATION: {
    name: "Content Drafting and Distribution Assistant",
    description:
      "Drafts collateral and now also shares workspace documents outward.",
    purposeCategory: "CONTENT_CREATION",
  },
  RESEARCH: {
    name: "Research Synthesis and Retrieval Assistant",
    description:
      "Synthesises briefings and now also retrieves from a broader article scope.",
    purposeCategory: "RESEARCH",
  },
};

export const getRecommendationCatalog = (): RecommendationCatalogEntry[] =>
  isInvestmentBankingDemo()
    ? [
        ...RECOMMENDATION_CATALOG,
        ...Object.values(DEMO_SCENARIOS).map(item => ({
          ...item.recommendation,
        })),
        ...DEMO_ADVISORY_RECOMMENDATIONS,
      ]
    : RECOMMENDATION_CATALOG;

export const getPlatformCatalog = (): AgentPlatformCatalogEntry[] =>
  PLATFORM_CATALOG.map((entry): AgentPlatformCatalogEntry => {
    if (!isInvestmentBankingDemo()) return entry;
    const beta = entry.availability === "BETA";
    return {
      ...entry,
      sampleConnection: {
        ...entry.sampleConnection,
        clientId: beta
          ? `demo-${entry.id.toLowerCase()}-client`
          : "demo-foundry-client",
      },
    };
  });
